import { AtomStoreContext } from "@atoms/atom-client";
import React, { useContext, useEffect, useRef, useState } from "react";
import type { DebugProtocol } from "./useDebugProtocolStore";

// Protocol bar component to properly handle hover state
interface ProtocolBarProps {
  protocol: DebugProtocol;
  relativeX: number;
  relativeWidth: number;
  isSelected: boolean;
  isFork: boolean;
  zoom: number;
  onSelectProtocol: (protocol: DebugProtocol) => void;
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  theme: any;
}

const ProtocolBar: React.FC<ProtocolBarProps> = ({
  protocol,
  relativeX,
  relativeWidth,
  isSelected,
  isFork,
  zoom,
  onSelectProtocol,
  theme,
}) => {
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <button
      type="button"
      style={{
        position: "absolute",
        left: `${relativeX}%`,
        width: `${relativeWidth}%`,
        height: "80%",
        top: "10%",
        background: protocol.error
          ? theme.errorColor
          : isFork
            ? theme.purpleColor
            : theme.blueColor,
        border: isSelected
          ? `2px solid ${theme.text}`
          : "1px solid rgba(255,255,255,0.3)",
        borderRadius: "4px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        transition: "all 0.2s",
        opacity: isHovered ? 1 : isSelected ? 1 : 0.9,
        zIndex: isHovered ? 3 : isSelected ? 2 : 1,
        transform: isHovered ? "scale(1.05)" : "scale(1)",
      }}
      onClick={(e) => {
        e.stopPropagation();
        onSelectProtocol(protocol);
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      title={`${protocol.operationName} - ${protocol.duration}ms${protocol.error ? " (ERROR)" : ""}`}
    >
      {zoom > 2 && relativeWidth > 0.5 && (
        <span
          style={{
            fontSize: "0.6rem",
            color: "white",
            fontWeight: "600",
            whiteSpace: "nowrap",
            padding: "0 4px",
          }}
        >
          {protocol.duration}ms
        </span>
      )}
    </button>
  );
};

interface TimelineOverviewProps {
  protocols: DebugProtocol[];
  onSelectProtocol: (protocol: DebugProtocol) => void;
  selectedProtocolId?: string;
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  theme: any;
}

interface ProtocolGroupProps {
  group: DebugProtocol[];
  groupStart: number;
  groupEnd: number;
  groupDuration: number;
  timelineBounds: { minTime: number; maxTime: number; totalDuration: number };
  zoom: number;
  onSelectProtocol: (protocol: DebugProtocol) => void;
  selectedProtocolId?: string;
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  theme: any;
}

const ProtocolGroup: React.FC<ProtocolGroupProps> = ({
  group,
  groupStart,
  groupEnd,
  groupDuration,
  timelineBounds,
  zoom,
  onSelectProtocol,
  selectedProtocolId,
  theme,
}) => {
  const getProtocolX = (timestamp: number) => {
    const relativeTime = timestamp - timelineBounds.minTime;
    return (relativeTime / timelineBounds.totalDuration) * 100;
  };

  const getProtocolWidth = (duration: number) => {
    return Math.max(0.1, (duration / timelineBounds.totalDuration) * 100);
  };

  const groupX = getProtocolX(groupStart);
  const groupWidth = getProtocolWidth(groupEnd - groupStart);

  // Find parent and fork protocols
  const parentProtocol = group.find((p) => !p.parentTransactionId && !p.isFork);
  const forkProtocols = group.filter((p) => p.isFork);
  const hasError = group.some((p) => p.error);

  // Calculate slow operation gap
  let slowOpStart = 0;
  let slowOpEnd = 0;
  let slowOpDuration = 0;
  let parentBarRight = 0;

  if (parentProtocol && forkProtocols.length > 0) {
    const parentEnd = parentProtocol.timestamp + parentProtocol.duration;
    const firstForkStart = Math.min(...forkProtocols.map((f) => f.timestamp));
    if (firstForkStart > parentEnd) {
      slowOpStart = ((parentEnd - groupStart) / groupDuration) * 100;
      slowOpEnd = ((firstForkStart - groupStart) / groupDuration) * 100;
      slowOpDuration = firstForkStart - parentEnd;
      // Calculate parent's right edge in percent of group
      const parentRelativeX =
        ((parentProtocol.timestamp - groupStart) / groupDuration) * 100;
      const parentRelativeWidth = Math.max(
        (parentProtocol.duration / groupDuration) * 100,
        2,
      );
      parentBarRight = parentRelativeX + parentRelativeWidth;
    }
  }

  // Helper to estimate if a label can fit based on zoom level
  // Show labels when zoomed in enough that items are visible
  const showLabels = zoom > 1.5;
  const _showDetailedLabels = zoom > 3;

  // Render slow op indicators for every significant gap between protocols
  const slowOpIndicators = [];
  if (group.length > 1) {
    for (let i = 0; i < group.length - 1; i++) {
      const current = group[i];
      const next = group[i + 1];
      if (!current || !next) {
        continue;
      }
      const currentEnd = current.timestamp + current.duration;
      if (next.timestamp > currentEnd) {
        // Calculate percent positions within group
        const gapStart = ((currentEnd - groupStart) / groupDuration) * 100;
        const gapEnd = ((next.timestamp - groupStart) / groupDuration) * 100;
        const gapDuration = next.timestamp - currentEnd;
        const gapWidth = gapEnd - gapStart;
        if (gapWidth > 0) {
          slowOpIndicators.push({
            left: gapStart,
            width: gapEnd - gapStart,
            center: (gapStart + gapEnd) / 2,
            duration: gapDuration,
            label: next.operationName,
          });
        }
      }
    }
  }

  return (
    <div
      style={{
        position: "absolute",
        left: `${groupX}%`,
        width: `${groupWidth}%`,
        height: "100%",
      }}
    >
      {/* Background */}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          height: "100%",
          background: hasError
            ? "rgba(220, 38, 38, 0.1)"
            : "rgba(255, 255, 255, 0.05)",
          border: `1px solid ${hasError ? "rgba(220, 38, 38, 0.3)" : "rgba(255, 255, 255, 0.1)"}`,
          borderRadius: "4px",
        }}
      />

      {/* Slow operation indicators for all gaps */}
      {slowOpIndicators.map((gap, _idx) => (
        <React.Fragment key={gap.left}>
          {/* Dashed line with right arrow, always visible */}
          <svg
            style={{
              position: "absolute",
              left: `${gap.left}%`,
              width: `${gap.width}%`,
              height: "12px",
              top: "calc(50% - 6px)",
              pointerEvents: "none",
              zIndex: 2,
            }}
            viewBox={"0 0 100 12"}
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            {/* Dashed line */}
            <line
              x1="0"
              y1="6"
              x2="92"
              y2="6"
              stroke="#fbbf24"
              strokeWidth="3"
              strokeDasharray="6,6"
            />
            {/* Arrowhead */}
            <polygon points="92,2 100,6 92,10" fill="#fbbf24" />
          </svg>
          {/* SLOW OP label: only if enough space */}
          {showLabels && gap.width > 2 && (
            <div
              style={{
                position: "absolute",
                left: `${gap.center}%`,
                top: "-12px",
                transform: "translateX(-50%)",
                background: "#fbbf24",
                color: "#000",
                fontSize: "0.6rem",
                fontWeight: "700",
                padding: "2px 6px",
                borderRadius: "3px",
                whiteSpace: "nowrap",
                boxShadow: "0 0 4px rgba(251, 191, 36, 0.5)",
                zIndex: 10,
                pointerEvents: "none",
              }}
            >
              {gap.label}
            </div>
          )}
        </React.Fragment>
      ))}

      {/* Render protocols */}
      {group.map((protocol) => {
        const relativeX =
          ((protocol.timestamp - groupStart) / groupDuration) * 100;
        // Strictly proportional width, only use 0.5px for zero/near-zero durations
        const proportionalWidth = (protocol.duration / groupDuration) * 100;
        const relativeWidth = proportionalWidth > 0 ? proportionalWidth : 0.1;
        const isSelected = protocol.id === selectedProtocolId;
        const isFork = protocol.isFork;

        return (
          <ProtocolBar
            key={protocol.id}
            protocol={protocol}
            relativeX={relativeX}
            relativeWidth={relativeWidth}
            isSelected={isSelected}
            isFork={isFork ?? false}
            zoom={zoom}
            onSelectProtocol={onSelectProtocol}
            theme={theme}
          />
        );
      })}

      {/* Connection lines */}
      {forkProtocols.map((fork) => {
        const parent = group.find(
          (p) => (p.transactionId || p.id) === fork.parentTransactionId,
        );
        if (!parent) return null;

        const parentX = ((parent.timestamp - groupStart) / groupDuration) * 100;
        const parentWidth = (parent.duration / groupDuration) * 100;
        const parentEndX = parentX + parentWidth;
        const forkX = ((fork.timestamp - groupStart) / groupDuration) * 100;

        return (
          <svg
            key={`line-${fork.id}`}
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
              overflow: "visible",
            }}
            aria-hidden="true"
          >
            {slowOpDuration > 0 ? (
              <>
                <path
                  d={`M ${parentEndX}% 50% L ${Math.max(slowOpStart, parentBarRight)}% 50%`}
                  stroke="rgba(255, 255, 255, 0.3)"
                  strokeWidth="1"
                  fill="none"
                />
                <path
                  d={`M ${Math.max(slowOpStart, parentBarRight)}% 50% Q ${(Math.max(slowOpStart, parentBarRight) + slowOpEnd) / 2}% 55% ${slowOpEnd}% 70%`}
                  stroke="rgba(255, 255, 255, 0.3)"
                  strokeWidth="1"
                  fill="none"
                />
              </>
            ) : (
              <path
                d={`M ${parentX + parentWidth / 2}% 50% Q ${(parentX + parentWidth / 2 + forkX) / 2}% 55% ${forkX}% 70%`}
                stroke="rgba(255, 255, 255, 0.3)"
                strokeWidth="1"
                fill="none"
                strokeDasharray="2,2"
              />
            )}
          </svg>
        );
      })}
    </div>
  );
};

type TimeFrame = "1m" | "1h" | "1d" | "1w" | "all";

const timeFrameMs: Record<TimeFrame, number> = {
  "1m": 60 * 1000,
  "1h": 60 * 60 * 1000,
  "1d": 24 * 60 * 60 * 1000,
  "1w": 7 * 24 * 60 * 60 * 1000,
  all: 0,
};

// Helper to find root parent
function _findRootParent(
  id: string,
  parentMap: Map<string, string>,
  visibleProtocols: DebugProtocol[],
): string {
  let current = id;
  const visited = new Set<string>();

  while (parentMap.has(current) && !visited.has(current)) {
    visited.add(current);
    const parent = parentMap.get(current);
    if (!parent) {
      break;
    }
    // Check if parent exists in visible protocols
    if (visibleProtocols.some((p) => (p.transactionId || p.id) === parent)) {
      current = parent;
    } else {
      break;
    }
  }

  return current;
}

export const TimelineOverview: React.FC<TimelineOverviewProps> = ({
  protocols,
  onSelectProtocol,
  selectedProtocolId,
  theme,
}) => {
  const [zoom, setZoom] = useState(1);
  const [offsetX, setOffsetX] = useState(0);
  const [timeFrame, setTimeFrame] = useState<TimeFrame>("1h");
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartOffset, setDragStartOffset] = useState(0);
  const atomStore = useContext(AtomStoreContext);
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  const [subscriptionEvents, setSubscriptionEvents] = useState<any[]>([]);

  // Calculate timeline bounds - always based on current time and selected timeframe
  const timelineBounds = React.useMemo(() => {
    const now = Date.now();

    if (timeFrame === "all" && protocols.length > 0) {
      // For 'all', show from first protocol to now
      const minTime = Math.min(...protocols.map((p) => p.timestamp));
      const maxTime = now;
      return { minTime, maxTime, totalDuration: maxTime - minTime };
    }
    // For time frames, always show from (now - timeframe) to now
    const duration = timeFrameMs[timeFrame] || timeFrameMs["1h"];
    const minTime = now - duration;
    const maxTime = now;
    return { minTime, maxTime, totalDuration: duration };
  }, [protocols, timeFrame]);

  useEffect(() => {
    if (atomStore && typeof atomStore.getSubscriptionEvents === "function") {
      setSubscriptionEvents(atomStore.getSubscriptionEvents());
    }
  }, [atomStore]);

  // Handle mouse wheel zoom using native event listener
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      const newZoom = Math.max(0.5, zoom * delta); // No upper limit on zoom

      // Zoom centered on mouse position
      const rect = container.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;

      // Calculate what time point is under the mouse
      const timeUnderMouse =
        timelineBounds.minTime +
        ((mouseX - offsetX) / (rect.width * zoom)) *
          timelineBounds.totalDuration;

      // Calculate new offset to keep the same time point under the mouse
      const newTimePosition =
        ((timeUnderMouse - timelineBounds.minTime) /
          timelineBounds.totalDuration) *
        rect.width *
        newZoom;
      const newOffset = mouseX - newTimePosition;

      setOffsetX(newOffset);
      setZoom(newZoom);
    };

    // Use passive: false to allow preventDefault
    container.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      container.removeEventListener("wheel", handleWheel);
    };
  }, [zoom, offsetX, timelineBounds]);

  // Handle dragging
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 0) {
      // Left click only
      setIsDragging(true);
      setDragStartX(e.clientX);
      setDragStartOffset(offsetX);
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      const deltaX = e.clientX - dragStartX;
      setOffsetX(dragStartOffset + deltaX);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (e: MouseEvent) => {
        const deltaX = e.clientX - dragStartX;
        setOffsetX(dragStartOffset + deltaX);
      };

      const handleGlobalMouseUp = () => {
        setIsDragging(false);
      };

      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleGlobalMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleGlobalMouseMove);
        document.removeEventListener("mouseup", handleGlobalMouseUp);
      };
    }
  }, [isDragging, dragStartX, dragStartOffset]);

  // Reset view to show full timeframe
  const resetView = () => {
    setZoom(1);
    setOffsetX(0);
  };

  const _getProtocolX = (protocol: DebugProtocol) => {
    const relativeTime = protocol.timestamp - timelineBounds.minTime;
    return (relativeTime / timelineBounds.totalDuration) * 100;
  };

  const _getProtocolWidth = (protocol: DebugProtocol) => {
    return Math.max(
      0.1,
      (protocol.duration / timelineBounds.totalDuration) * 100,
    );
  };

  // Filter protocols by time frame - show all protocols that are at least partially in view
  const visibleProtocols = React.useMemo(() => {
    return protocols.filter((p) => {
      const protocolEnd = p.timestamp + p.duration;
      return (
        protocolEnd >= timelineBounds.minTime &&
        p.timestamp <= timelineBounds.maxTime
      );
    });
  }, [protocols, timelineBounds]);

  // Group protocols by parent-child relationships (ONLY by transactionId/parentId)
  const protocolGroups = React.useMemo(() => {
    const protocolsById = new Map<string, DebugProtocol>();
    for (const p of visibleProtocols) {
      if (p.transactionId) protocolsById.set(p.transactionId, p);
      else if (p.id) protocolsById.set(p.id, p);
    }

    // Helper to find the root ancestor for a protocol
    function findRoot(protocol: DebugProtocol): string {
      let current = protocol;
      let safety = 0;
      while (
        current.parentTransactionId &&
        protocolsById.has(current.parentTransactionId) &&
        safety < 20
      ) {
        const parent = protocolsById.get(current.parentTransactionId);
        if (!parent) {
          break;
        }
        current = parent;
        safety++;
      }
      return current.transactionId || current.id;
    }

    // Group protocols by their root ancestor
    const groups: Map<string, DebugProtocol[]> = new Map();
    for (const protocol of visibleProtocols) {
      const rootId = findRoot(protocol);
      if (!groups.has(rootId)) groups.set(rootId, []);
      groups.get(rootId)?.push(protocol);
    }

    // Sort each group by timestamp
    for (const group of groups.values()) {
      group.sort((a, b) => a.timestamp - b.timestamp);
    }

    return groups;
  }, [visibleProtocols]);

  // Group overlapping protocol groups into rows
  const rows = React.useMemo(() => {
    const rows: Array<{ group: DebugProtocol[]; rootId: string }[]> = [];
    const sortedGroups = Array.from(protocolGroups.entries())
      .map(([rootId, group]) => ({ rootId, group }))
      .sort((a, b) => {
        const aStart = Math.min(...a.group.map((p) => p.timestamp));
        const bStart = Math.min(...b.group.map((p) => p.timestamp));
        return aStart - bStart;
      });

    for (const { rootId, group } of sortedGroups) {
      let placed = false;
      const groupStart = Math.min(...group.map((p) => p.timestamp));
      const groupEnd = Math.max(...group.map((p) => p.timestamp + p.duration));
      const _groupDuration = groupEnd - groupStart;

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        if (!row || row.length === 0) continue;

        // Check if this group overlaps with any group in this row
        const overlaps = row.some(({ group: existingGroup }) => {
          const existingStart = Math.min(
            ...existingGroup.map((p) => p.timestamp),
          );
          const existingEnd = Math.max(
            ...existingGroup.map((p) => p.timestamp + p.duration),
          );

          return !(groupEnd <= existingStart || groupStart >= existingEnd);
        });

        if (!overlaps) {
          row.push({ rootId, group });
          placed = true;
          break;
        }
      }

      if (!placed) {
        rows.push([{ rootId, group }]);
      }
    }

    return rows;
  }, [protocolGroups]);

  return (
    <div
      style={{
        background: theme.background || theme.cardBg,
        border: `1px solid ${theme.borderColor}`,
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "0.75rem 1rem",
          borderBottom: `1px solid ${theme.borderColor}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h3 style={{ margin: 0, fontSize: "1rem", color: theme.text }}>
          Timeline Overview ({visibleProtocols.length} of {protocols.length}{" "}
          operations)
        </h3>
        <div
          style={{
            display: "flex",
            gap: "1rem",
            alignItems: "center",
            fontSize: "0.875rem",
          }}
        >
          <span style={{ color: theme.subtextColor }}>
            Scroll to zoom, drag to pan
          </span>

          <div
            style={{ display: "flex", gap: "0.25rem", alignItems: "center" }}
          >
            <span style={{ color: theme.subtextColor, fontSize: "0.75rem" }}>
              Time frame:
            </span>
            {(["1m", "1h", "1d", "1w", "all"] as TimeFrame[]).map((tf) => (
              <button
                key={tf}
                type="button"
                onClick={() => {
                  setTimeFrame(tf);
                  resetView();
                }}
                style={{
                  padding: "0.25rem 0.5rem",
                  background:
                    timeFrame === tf ? theme.blueColor : theme.buttonBg,
                  color: timeFrame === tf ? "white" : theme.buttonText,
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "0.7rem",
                  fontWeight: timeFrame === tf ? "600" : "400",
                }}
              >
                {tf.toUpperCase()}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={resetView}
            style={{
              padding: "0.25rem 0.75rem",
              background: theme.buttonBg,
              color: theme.buttonText,
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "0.75rem",
            }}
          >
            Reset View
          </button>
          <span style={{ color: theme.subtextColor }}>
            Zoom: {(zoom * 100).toFixed(0)}%
          </span>
        </div>
      </div>

      <div
        ref={containerRef}
        role="application"
        style={{
          position: "relative",
          height: `${Math.max(rows.length * 40 + 60, 120)}px`,
          overflow: "hidden",
          cursor: isDragging ? "grabbing" : "grab",
          userSelect: "none",
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: `${100 * zoom}%`,
            height: "100%",
            transform: `translateX(${offsetX}px)`,
            transformOrigin: "left top",
          }}
        >
          {/* Time scale markers */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "30px",
              borderBottom: `1px solid ${theme.borderColor}`,
              display: "flex",
              alignItems: "flex-end",
            }}
          >
            {Array.from(
              { length: Math.min(50, Math.ceil(10 * zoom)) },
              (val, i) => {
                const percent = (i / Math.ceil(10 * zoom)) * 100;
                const time =
                  timelineBounds.minTime +
                  (percent / 100) * timelineBounds.totalDuration;

                return (
                  <div
                    key={`time-marker-${val ?? i}`}
                    style={{
                      position: "absolute",
                      left: `${percent}%`,
                      bottom: 0,
                      width: "1px",
                      height: "10px",
                      background: theme.borderColor,
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        bottom: "12px",
                        left: "-20px",
                        width: "40px",
                        textAlign: "center",
                        fontSize: "0.65rem",
                        color: theme.subtextColor,
                      }}
                    >
                      {(() => {
                        const date = new Date(time);
                        const duration = timelineBounds.totalDuration / zoom;

                        // For short durations (< 1 minute), show seconds with milliseconds
                        if (duration < 60 * 1000) {
                          return date
                            .toLocaleTimeString("en-US", {
                              hour12: false,
                              minute: "2-digit",
                              second: "2-digit",
                              fractionalSecondDigits: 1,
                            })
                            .split(":")
                            .slice(-2)
                            .join(":");
                        }
                        // For medium durations (< 1 hour), show minutes:seconds
                        if (duration < 60 * 60 * 1000) {
                          return date
                            .toLocaleTimeString("en-US", {
                              hour12: false,
                              minute: "2-digit",
                              second: "2-digit",
                            })
                            .split(":")
                            .slice(-2)
                            .join(":");
                        }
                        // For longer durations, show hour:minute

                        return date.toLocaleTimeString("en-US", {
                          hour12: false,
                          hour: "2-digit",
                          minute: "2-digit",
                        });
                      })()}
                    </span>
                  </div>
                );
              },
            )}
          </div>

          {/* Now marker */}
          <div
            style={{
              position: "absolute",
              right: 0,
              top: 0,
              height: "100%",
              width: "2px",
              background: theme.errorColor,
              zIndex: 15,
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                position: "absolute",
                right: "-20px",
                top: "35px",
                background: theme.errorColor,
                color: "white",
                padding: "2px 6px",
                fontSize: "0.7rem",
                fontWeight: "600",
                borderRadius: "3px",
                whiteSpace: "nowrap",
              }}
            >
              NOW
            </div>
          </div>

          {/* Atom subscription markers */}
          {subscriptionEvents && subscriptionEvents.length > 0 && (
            <>
              {subscriptionEvents.map((event, _idx) => {
                // Position marker based on event.timestamp
                const percent =
                  timelineBounds.totalDuration > 0
                    ? ((event.timestamp - timelineBounds.minTime) /
                        timelineBounds.totalDuration) *
                      100
                    : 0;
                if (percent < 0 || percent > 100) return null;
                return (
                  <>
                    <div
                      style={{
                        position: "absolute",
                        top: 30,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "2px",
                        height: "calc(100% - 30px)",
                        background: "#22c55e",
                        boxShadow: "0 0 4px #22c55e",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        top: 10,
                        left: "50%",
                        transform: "translateX(-50%)",
                        background: "#22c55e",
                        color: "#000",
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        padding: "2px 6px",
                        borderRadius: "3px",
                        whiteSpace: "nowrap",
                        boxShadow: "0 0 4px #22c55e",
                        zIndex: 21,
                      }}
                    >
                      SUB: {event.atomType}
                    </div>
                  </>
                );
              })}

              {/* Protocol bars */}
              {rows.map((row, rowIndex) => (
                <div
                  key={row.map((r) => r.rootId).join("-")}
                  style={{
                    position: "absolute",
                    top: `${30 + rowIndex * 40}px`,
                    left: 0,
                    right: 0,
                    height: "30px",
                  }}
                >
                  {row.map(({ group, rootId }) => {
                    const groupStart = Math.min(
                      ...group.map((p) => p.timestamp),
                    );
                    const groupEnd = Math.max(
                      ...group.map((p) => p.timestamp + p.duration),
                    );
                    const groupDuration = groupEnd - groupStart;

                    return (
                      <ProtocolGroup
                        key={`${rootId}-${group.map((p) => p.id).join("-")}`}
                        group={group}
                        groupStart={groupStart}
                        groupEnd={groupEnd}
                        groupDuration={groupDuration}
                        timelineBounds={timelineBounds}
                        zoom={zoom}
                        onSelectProtocol={onSelectProtocol}
                        selectedProtocolId={selectedProtocolId}
                        theme={theme}
                      />
                    );
                  })}
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
